import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import GlobalStyles from "./GlobalStyles";

// Fallback token for deployments where build-time env vars are not injected.
// Replace with your real public Mapbox token if needed.
const HARDCODED_MAPBOX_TOKEN = "pk.eyJ1Ijoiem9oYWlic2hhaGlkIiwiYSI6ImNtcGM5Y3QwMzA1eWYycnF2Y2RqOTRncGYifQ.KzSRg23VFKDUm98h9SRb-g";

const MAPBOX_TOKEN = (
  process.env.REACT_APP_MAPBOX_API_KEY || HARDCODED_MAPBOX_TOKEN || ""
).trim();
if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

const FALLBACK_MAP_STYLE = {
  version: 8,
  sources: {},
  layers: [
    {
      id: "background",
      type: "background",
      paint: {
        "background-color": "#d8dde6",
      },
    },
  ],
};

const MAP_STYLE =
  process.env.REACT_APP_MAP_STYLE_URL ||
  (MAPBOX_TOKEN
    ? "mapbox://styles/mapbox/satellite-streets-v11"
    : FALLBACK_MAP_STYLE);

const PAKISTAN_CENTER = [73.0479, 33.6844];

const MapComponent = ({
  latitude,
  longitude,
  onLocationChange,
  onMapReady,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const ignoreInitialProps = useRef(true);
  const unitsGeometryRef = useRef(null);
  const switchedToFallbackRef = useRef(false);
  const usingFallbackStyleRef = useRef(!MAPBOX_TOKEN);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [pakistanGeometry, setPakistanGeometry] = useState(null);
  const [error, setError] = useState(null);
  const [unitsGeometry, setUnitsGeometry] = useState(null);
  const [detectedProvince, setDetectedProvince] = useState(null);

  const [selectedLat, setSelectedLat] = useState(
    latitude ? parseFloat(latitude).toFixed(2) : (33.6844).toFixed(2)
  );
  const [selectedLng, setSelectedLng] = useState(
    longitude ? parseFloat(longitude).toFixed(2) : (73.0479).toFixed(2)
  );

  const mapContainerStyle = {
    height: "84vh",
    width: "100%",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  };

  useEffect(() => {
    const loadPakistanData = async () => {
      try {
        const response = await fetch(
          process.env.PUBLIC_URL + "/json/pakistan.json"
        );
        if (!response.ok) {
          throw new Error("Failed to load Pakistan GeoJSON data");
        }
        const data = await response.json();

        if (
          data.type === "FeatureCollection" &&
          data.features &&
          data.features.length > 0
        ) {
          setPakistanGeometry(data.features[0].geometry);
        } else if (data.type === "Feature") {
          setPakistanGeometry(data.geometry);
        } else {
          throw new Error("Invalid GeoJSON format");
        }
      } catch (err) {
        console.error("Error loading Pakistan GeoJSON:", err);
        setError("Failed to load Pakistan boundary data");
      }
    };
    loadPakistanData();
  }, []);

  useEffect(() => {
    const loadUnitsData = async () => {
      try {
        const response = await fetch(
          process.env.PUBLIC_URL + "/json/units.json"
        );

        if (response.ok) {
          const data = await response.json();
          setUnitsGeometry(data);
          unitsGeometryRef.current = data;
        } else {
          console.warn("Failed to load units.json");
        }
      } catch (err) {
        console.warn("Error loading units.json:", err);
      }
    };
    loadUnitsData();
  }, []);

  const isPointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];

      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  };

  const isWithinPakistan = (lng, lat) => {
    if (!pakistanGeometry) return true;
    const point = [lng, lat];

    if (pakistanGeometry.type === "MultiPolygon") {
      for (const polygon of pakistanGeometry.coordinates) {
        if (isPointInPolygon(point, polygon[0])) {
          let inHole = false;
          for (let i = 1; i < polygon.length; i++) {
            if (isPointInPolygon(point, polygon[i])) {
              inHole = true;
              break;
            }
          }
          if (!inHole) return true;
        }
      }
      return false;
    }

    if (pakistanGeometry.type === "Polygon") {
      if (isPointInPolygon(point, pakistanGeometry.coordinates[0])) {
        for (let i = 1; i < pakistanGeometry.coordinates.length; i++) {
          if (isPointInPolygon(point, pakistanGeometry.coordinates[i])) {
            return false;
          }
        }
        return true;
      }
    }
    return false;
  };

  const constrainToPakistan = (lng, lat) => {
    if (!pakistanGeometry || isWithinPakistan(lng, lat)) {
      return [lng, lat];
    }
    return PAKISTAN_CENTER;
  };

  const detectProvince = (lng, lat) => {
    const dataToUse = unitsGeometryRef.current || unitsGeometry;

    if (!dataToUse?.features) {
      return null;
    }

    const point = [lng, lat];

    for (let i = 0; i < dataToUse.features.length; i++) {
      const feature = dataToUse.features[i];
      const geometry = feature.geometry;
      const properties = feature.properties || {};

      if (geometry.type === "Polygon") {
        if (isPointInPolygon(point, geometry.coordinates[0])) {
          const provinceName =
            properties.NAME_1 || properties.name || properties.NAME;

          const standardized = provinceName?.toLowerCase().includes("punjab")
            ? "Punjab"
            : provinceName?.toLowerCase().includes("sindh")
            ? "Sindh"
            : provinceName?.toLowerCase().includes("baloch")
            ? "Balochistan"
            : provinceName?.toLowerCase().includes("khyber") ||
              provinceName?.toLowerCase().includes("kpk")
            ? "KPK"
            : provinceName;

          return standardized;
        }
      } else if (geometry.type === "MultiPolygon") {
        for (const polygon of geometry.coordinates) {
          if (isPointInPolygon(point, polygon[0])) {
            const provinceName =
              properties.NAME_1 || properties.name || properties.NAME;
            const standardized = provinceName?.toLowerCase().includes("punjab")
              ? "Punjab"
              : provinceName?.toLowerCase().includes("sindh")
              ? "Sindh"
              : provinceName?.toLowerCase().includes("baloch")
              ? "Balochistan"
              : provinceName?.toLowerCase().includes("khyber") ||
                provinceName?.toLowerCase().includes("kpk")
              ? "KPK"
              : provinceName;
            return standardized;
          }
        }
      }
    }
    return null;
  };

  const isMapReady = () => {
    return (
      map.current && mapLoaded && styleLoaded && map.current.isStyleLoaded()
    );
  };

  const addLayersWhenReady = () => {
    if (!isMapReady() || !pakistanGeometry) return;

    try {

      if (map.current.getSource("pakistan-border")) {
        return;
      }

      addPakistanLayers();
    } catch (error) {
      console.error("Error adding layers:", error);
    }
  };

  const handleInitialProvinceDetection = () => {
    const lat = parseFloat(selectedLat);
    const lng = parseFloat(selectedLng);

    const province = detectProvince(lng, lat);
    setDetectedProvince(province);

    if (onLocationChange) {
      onLocationChange(lat, lng, province);
    }
  };

  useEffect(() => {
    if (onMapReady && isMapReady()) {
      const mapActions = {
        updateMarkerPosition: (lat, lng) => {
          const [constrainedLng, constrainedLat] = constrainToPakistan(
            lng,
            lat
          );
          if (marker.current) {
            marker.current.setLngLat([constrainedLng, constrainedLat]);
            map.current.flyTo({
              center: [constrainedLng, constrainedLat],
              zoom: map.current.getZoom(),
              essential: true,
            });
          }
          const province = detectProvince(constrainedLng, constrainedLat);
          setDetectedProvince(province);
          if (onLocationChange) {
            onLocationChange(constrainedLat, constrainedLng, province);
          }
        },
        resetMap: (resetLat, resetLng) => {
          const [constrainedLng, constrainedLat] = constrainToPakistan(
            resetLng,
            resetLat
          );
          if (marker.current) {
            marker.current.setLngLat([constrainedLng, constrainedLat]);
            map.current.flyTo({
              center: [constrainedLng, constrainedLat],
              zoom: 5,
              essential: true,
            });
          }
        },
      };
      onMapReady(mapActions);
    }
  }, [mapLoaded, styleLoaded, pakistanGeometry]);

  useEffect(() => {
    if (latitude) {
      setSelectedLat(parseFloat(latitude).toFixed(2));
    }
  }, [latitude]);

  useEffect(() => {
    if (longitude) {
      setSelectedLng(parseFloat(longitude).toFixed(2));
    }
  }, [longitude]);

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: [parseFloat(selectedLng), parseFloat(selectedLat)],
      zoom: 5.5,
      attributionControl: true,
      minZoom: 5,
      maxZoom: 18,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.current.addControl(new mapboxgl.FullscreenControl(), "top-right");
    map.current.addControl(new mapboxgl.ScaleControl(), "bottom-left");

    map.current.on("style.load", () => {
      setStyleLoaded(true);
    });

    map.current.on("load", () => {
      setMapLoaded(true);

      if (selectedLat && selectedLng) {
        const [constrainedLng, constrainedLat] = constrainToPakistan(
          parseFloat(selectedLng),
          parseFloat(selectedLat)
        );

        marker.current = new mapboxgl.Marker({
          draggable: true,
          color: "#3FB1CE",
        })
          .setLngLat([constrainedLng, constrainedLat])
          .addTo(map.current);

        if (
          constrainedLng !== parseFloat(selectedLng) ||
          constrainedLat !== parseFloat(selectedLat)
        ) {
          setSelectedLng(constrainedLng.toFixed(2));
          setSelectedLat(constrainedLat.toFixed(2));
        }

        setupMarkerDragHandler();
      }

      setupMapClickHandler();
    });

    map.current.on("data", (e) => {
      if (e.type === "data" && e.isSourceLoaded) {
      }
    });

    map.current.on("styledata", () => {
      if (map.current.isStyleLoaded()) {
        setStyleLoaded(true);
      }
    });

    map.current.on("error", (e) => {
      const mapError = e?.error;
      if (!mapError) return;

      const message = mapError.message || "";
      console.error("Map error:", mapError);

      const isUnauthorizedMapboxError =
        /403|401|forbidden|unauthorized|access token/i.test(message);

      // If Mapbox token/style is blocked (domain restriction, bad scope, or invalid token),
      // switch once to the local fallback style so the map remains usable.
      if (
        isUnauthorizedMapboxError &&
        !switchedToFallbackRef.current &&
        map.current
      ) {
        switchedToFallbackRef.current = true;
        usingFallbackStyleRef.current = true;
        setError(null);
        map.current.setStyle(FALLBACK_MAP_STYLE);
        return;
      }

      // Ignore noisy/non-fatal tile fetch issues in fallback mode.
      const isLikelyTileFetchIssue = /failed to fetch|networkerror|load failed/i.test(
        message
      );
      if (usingFallbackStyleRef.current && isLikelyTileFetchIssue) {
        return;
      }

      // In fallback mode we intentionally keep a minimal map and avoid blocking UI with noisy errors.
      if (usingFallbackStyleRef.current) {
        return;
      }

      setError("Error loading map. Please check your internet connection.");
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  const addPakistanLayers = () => {
    if (!map.current || !pakistanGeometry) return;

    try {
      if (!usingFallbackStyleRef.current) {
        map.current.addSource("world-mask", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [-180, -90],
                  [180, -90],
                  [180, 90],
                  [-180, 90],
                  [-180, -90],
                ],
                ...(pakistanGeometry.type === "MultiPolygon"
                  ? pakistanGeometry.coordinates.map((polygon) => polygon[0])
                  : [pakistanGeometry.coordinates[0]]),
              ],
            },
          },
        });

        map.current.addLayer({
          id: "world-mask-layer",
          type: "fill",
          source: "world-mask",
          paint: {
            "fill-color": "#ffffff",
            "fill-opacity": 0.85,
          },
        });
      }

      map.current.addSource("pakistan-border", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: pakistanGeometry,
        },
      });

      if (usingFallbackStyleRef.current) {
        map.current.addLayer({
          id: "pakistan-fill",
          type: "fill",
          source: "pakistan-border",
          paint: {
            "fill-color": "#1f3b63",
            "fill-opacity": 0.25,
          },
        });
      }

      map.current.addLayer({
        id: "pakistan-border-line",
        type: "line",
        source: "pakistan-border",
        paint: {
          "line-color": "#2563eb",
          "line-width": 3,
          "line-opacity": 0.8,
        },
      });
    } catch (error) {
      console.error("Error adding Pakistan layers:", error);
    }
  };

  const setupMarkerDragHandler = () => {
    if (!marker.current) return;

    marker.current.on("dragend", () => {
      const { lng, lat } = marker.current.getLngLat();

      if (!isWithinPakistan(lng, lat)) {
        const [constrainedLng, constrainedLat] = constrainToPakistan(lng, lat);
        marker.current.setLngLat([constrainedLng, constrainedLat]);

        const fixedLng = constrainedLng.toFixed(2);
        const fixedLat = constrainedLat.toFixed(2);
        setSelectedLng(fixedLng);
        setSelectedLat(fixedLat);

        const province = detectProvince(constrainedLng, constrainedLat);
        setDetectedProvince(province);
        if (onLocationChange) {
          onLocationChange(
            parseFloat(fixedLat),
            parseFloat(fixedLng),
            province
          );
        }

        setError("Location adjusted to stay within Pakistan boundaries");
        setTimeout(() => setError(null), 3000);
      } else {
        const fixedLng = lng.toFixed(2);
        const fixedLat = lat.toFixed(2);
        setSelectedLng(fixedLng);
        setSelectedLat(fixedLat);

        const province = detectProvince(lng, lat);
        setDetectedProvince(province);
        if (onLocationChange) {
          onLocationChange(
            parseFloat(fixedLat),
            parseFloat(fixedLng),
            province
          );
        }
      }
    });
  };

  const setupMapClickHandler = () => {
    if (!map.current) return;

    map.current.on("click", (e) => {
      const { lng, lat } = e.lngLat;

      if (!isWithinPakistan(lng, lat)) {
        setError("Please select a location within Pakistan");
        setTimeout(() => setError(null), 3000);
        return;
      }

      const fixedLng = lng.toFixed(2);
      const fixedLat = lat.toFixed(2);

      setSelectedLng(fixedLng);
      setSelectedLat(fixedLat);

      if (marker.current) {
        marker.current.setLngLat([parseFloat(fixedLng), parseFloat(fixedLat)]);
      } else {
        marker.current = new mapboxgl.Marker({
          draggable: true,
          color: "#3FB1CE",
        })
          .setLngLat([parseFloat(fixedLng), parseFloat(fixedLat)])
          .addTo(map.current);

        setupMarkerDragHandler();
      }

      const province = detectProvince(
        parseFloat(fixedLng),
        parseFloat(fixedLat)
      );
      setDetectedProvince(province);
      if (onLocationChange) {
        onLocationChange(parseFloat(fixedLat), parseFloat(fixedLng), province);
      }
    });
  };

  useEffect(() => {
    addLayersWhenReady();
  }, [mapLoaded, styleLoaded, pakistanGeometry]);

  useEffect(() => {
    if (unitsGeometry && (!detectedProvince || detectedProvince === null)) {
      handleInitialProvinceDetection();
    }
  }, [unitsGeometry, selectedLat, selectedLng]);

  useEffect(() => {
    if (
      unitsGeometryRef.current &&
      (!detectedProvince || detectedProvince === null)
    ) {
      handleInitialProvinceDetection();
    }
  }, [unitsGeometryRef.current]);

  useEffect(() => {
    if (ignoreInitialProps.current) {
      ignoreInitialProps.current = false;
      return;
    }

    if (marker.current && latitude && longitude && isMapReady()) {
      const [constrainedLng, constrainedLat] = constrainToPakistan(
        longitude,
        latitude
      );

      marker.current.setLngLat([constrainedLng, constrainedLat]);
      map.current.flyTo({
        center: [constrainedLng, constrainedLat],
        zoom: map.current.getZoom(),
        essential: true,
      });

      if (constrainedLng !== longitude || constrainedLat !== latitude) {
        setSelectedLng(constrainedLng.toFixed(2));
        setSelectedLat(constrainedLat.toFixed(2));
      }

      const province = detectProvince(constrainedLng, constrainedLat);
      setDetectedProvince(province);
      if (onLocationChange) {
        onLocationChange(constrainedLat, constrainedLng, province);
      }
    }
  }, [latitude, longitude, mapLoaded, styleLoaded]);

  return (
    <div className="map-container">
      <div
        ref={mapContainer}
        className="mapboxgl-map"
        style={mapContainerStyle}
      />

      {}
      {(!mapLoaded || !styleLoaded) && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "10px" }}>??? Loading Map...</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {!mapLoaded ? "Initializing map..." : "Loading map style..."}
            </div>
          </div>
        </div>
      )}

      {error && (
        <p
          style={{
            ...GlobalStyles.error,
            fontSize: "0.9rem",
            marginTop: "5px",
            backgroundColor: error.includes("adjusted") ? "#fff3cd" : "#f8d7da",
            color: error.includes("adjusted") ? "#856404" : "#721c24",
            padding: "8px 12px",
            borderRadius: "4px",
            border: `1px solid ${
              error.includes("adjusted") ? "#ffeaa7" : "#f5c6cb"
            }`,
          }}
        >
          {error}
        </p>
      )}

      <p style={GlobalStyles.info}>
        Click on the map to select a location within Pakistan, then click Submit
        to process the data. Selected coordinates: {selectedLat}, {selectedLng}
      </p>
    </div>
  );
};

export default MapComponent;
